import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtINR, fmtDate, fmtDateTime } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Calendar as CalendarIcon, ChevronRight, FileText, Receipt, Upload, Wrench,
  MapPin, User as UserIcon, Save, ClipboardList, Camera, Download,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_FLOW = ["scheduling", "execution", "analysis", "invoice", "completed"];
const STAGE_LABEL = {
  scheduling: "Scheduling",
  execution:  "Job execution",
  analysis:   "Analysis & report",
  invoice:    "Invoice submission",
  completed:  "Completed",
};

const NOTE_KIND_BY_STAGE = {
  scheduling: "scheduling",
  execution:  "execution",
  analysis:   "analysis",
  invoice:    "payment",
};

function StageProgress({ current }) {
  const idx = STAGE_FLOW.indexOf(current);
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="service-job-stage-progress">
      {STAGE_FLOW.map((s, i) => {
        const done = i <= idx;
        const active = i === idx;
        return (
          <div key={s} className="flex items-center gap-2 shrink-0">
            <div
              className={`h-8 px-3 rounded-2xl text-[11px] font-bold tracking-wide inline-flex items-center border ${
                active
                  ? "bg-primary text-primary-foreground border-primary shadow-soft"
                  : done
                    ? "bg-primary-50 text-primary-800 border-primary-200"
                    : "bg-muted/40 text-muted-foreground border-border/60"
              }`}
              data-testid={`stage-pill-${s}`}
            >
              {STAGE_LABEL[s]}
            </div>
            {i < STAGE_FLOW.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />}
          </div>
        );
      })}
    </div>
  );
}

function NoteList({ items }) {
  if (!items?.length) return <p className="text-xs text-muted-foreground italic">No notes yet.</p>;
  return (
    <ul className="space-y-2">
      {items.map((n) => (
        <li key={n.id} className="rounded-2xl border border-border/60 p-3" data-testid={`note-row-${n.id}`}>
          <p className="text-sm whitespace-pre-wrap">{n.text}</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            {n.by_name || "—"} · {n.date} · {fmtDateTime(n.created_at)}
          </p>
        </li>
      ))}
    </ul>
  );
}

function AddNote({ kind, jobId, onAdded }) {
  const [text, setText] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/service-jobs/${jobId}/notes/${kind}`, { text, date });
      setText("");
      toast.success("Logged");
      onAdded?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
      <div className="space-y-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Log a discussion, visit confirmation, or activity…" data-testid={`note-input-${kind}`} className="rounded-2xl" />
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-9 w-44" />
        </div>
      </div>
      <Button onClick={submit} disabled={busy || !text.trim()} className="rounded-2xl h-11" data-testid={`note-add-${kind}`}>
        <Save className="h-4 w-4 mr-1.5" /> Log
      </Button>
    </div>
  );
}

function FileUploader({ jobId, kind, label, onUploaded, accept }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/service-jobs/${jobId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onUploaded?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`upload-input-${kind}`} />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} className="rounded-2xl" data-testid={`upload-btn-${kind}`}>
        <Upload className="h-3.5 w-3.5 mr-1.5" /> {label}
      </Button>
    </div>
  );
}

export default function ServiceJobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engineers, setEngineers] = useState([]);
  const [tab, setTab] = useState("assignment");
  const [savingAssign, setSavingAssign] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/service-jobs/${jobId}`);
      setJob(data);
      // Engineers list — anyone with role 'service' or 'salesperson' or 'manager'.
      try {
        const us = (await api.get("/users", { params: { active: true } })).data || [];
        setEngineers(us.filter((u) => ["service", "salesperson", "manager", "admin"].includes(u.role)));
      } catch { /* silent */ }
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/service-jobs/${jobId}`, body);
      setJob(data);
      toast.success("Saved");
    } catch (err) { toast.error(fmtError(err)); }
  };

  const moveStage = async (next) => {
    try {
      await api.post(`/service-jobs/${jobId}/stage`, { stage: next });
      await load();
      toast.success(`Moved to ${STAGE_LABEL[next]}`);
    } catch (err) { toast.error(fmtError(err)); }
  };

  if (loading || !job) {
    return (
      <div className="p-6 sm:p-8 space-y-4">
        <Skeleton className="h-10 w-64 rounded-2xl" />
        <Skeleton className="h-40 rounded-3xl" />
        <Skeleton className="h-60 rounded-3xl" />
      </div>
    );
  }

  const idx = STAGE_FLOW.indexOf(job.stage);
  const nextStage = idx >= 0 && idx < STAGE_FLOW.length - 1 ? STAGE_FLOW[idx + 1] : null;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-5xl">
      <Link to="/service-execution" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground gap-1.5 mb-4" data-testid="back-to-service-execution">
        <ArrowLeft className="h-4 w-4" /> All service jobs
      </Link>

      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Service Job</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1" data-testid="service-job-title">
          {job.deal?.title || "Untitled"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {job.client?.company_name || "—"} · {fmtINR(job.deal?.value)}
        </p>
      </header>

      <Card className="rounded-3xl border-border/60 mb-5">
        <CardContent className="p-5 sm:p-6">
          <StageProgress current={job.stage} />
          {nextStage && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button onClick={() => moveStage(nextStage)} className="rounded-2xl" data-testid="advance-stage-btn">
                Advance to {STAGE_LABEL[nextStage]} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <span className="text-xs text-muted-foreground">Stage moves are one-way · ensure the current stage is fully completed.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="rounded-2xl bg-muted/60 p-1 flex flex-wrap h-auto">
          <TabsTrigger value="assignment"  data-testid="tab-assignment"  className="rounded-xl text-xs">Assignment</TabsTrigger>
          <TabsTrigger value="scheduling"  data-testid="tab-scheduling"  className="rounded-xl text-xs"><CalendarIcon className="h-3.5 w-3.5 mr-1" />Scheduling</TabsTrigger>
          <TabsTrigger value="execution"   data-testid="tab-execution"   className="rounded-xl text-xs"><Wrench className="h-3.5 w-3.5 mr-1" />Execution</TabsTrigger>
          <TabsTrigger value="analysis"    data-testid="tab-analysis"    className="rounded-xl text-xs"><FileText className="h-3.5 w-3.5 mr-1" />Analysis</TabsTrigger>
          <TabsTrigger value="invoice"     data-testid="tab-invoice"     className="rounded-xl text-xs"><Receipt className="h-3.5 w-3.5 mr-1" />Invoice</TabsTrigger>
          <TabsTrigger value="files"       data-testid="tab-files"       className="rounded-xl text-xs"><Camera className="h-3.5 w-3.5 mr-1" />Photos & Docs</TabsTrigger>
        </TabsList>

        {/* ASSIGNMENT */}
        <TabsContent value="assignment">
          <Card className="rounded-3xl border-border/60">
            <CardContent className="p-5 space-y-4">
              <p className="font-display text-lg font-extrabold">Assignment & scope</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Execute as</Label>
                  <div className="flex gap-2 mt-1.5" data-testid="execute-as-toggle">
                    <button
                      type="button"
                      onClick={() => patch({ self_execute: true })}
                      className={`flex-1 h-11 rounded-2xl border text-sm font-semibold tap ${job.self_execute ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border/60"}`}
                      data-testid="execute-self"
                    >Execute myself</button>
                    <button
                      type="button"
                      onClick={() => patch({ self_execute: false })}
                      className={`flex-1 h-11 rounded-2xl border text-sm font-semibold tap ${!job.self_execute ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border/60"}`}
                      data-testid="execute-engineer"
                    >Assign engineer</button>
                  </div>
                </div>
                {!job.self_execute && (
                  <div>
                    <Label className="text-xs font-semibold">Assigned engineer</Label>
                    <Select value={job.assigned_engineer_id || "__none"} onValueChange={(v) => patch({ assigned_engineer_id: v === "__none" ? null : v })}>
                      <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="engineer-select"><SelectValue placeholder="Pick an engineer" /></SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="__none">— Not assigned —</SelectItem>
                        {engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold">Tentative start</Label>
                  <Input type="date" className="rounded-2xl h-11 mt-1.5" value={(job.tentative_schedule_start || "").slice(0, 10)} onChange={(e) => patch({ tentative_schedule_start: e.target.value || null })} data-testid="tentative-start" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Tentative end</Label>
                  <Input type="date" className="rounded-2xl h-11 mt-1.5" value={(job.tentative_schedule_end || "").slice(0, 10)} onChange={(e) => patch({ tentative_schedule_end: e.target.value || null })} data-testid="tentative-end" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Duration (days)</Label>
                  <Input type="number" min="1" className="rounded-2xl h-11 mt-1.5" value={job.service_duration_days || ""} onChange={(e) => patch({ service_duration_days: Number(e.target.value) || null })} data-testid="service-duration" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Site location</Label>
                  <Input className="rounded-2xl h-11 mt-1.5" value={job.site_location || ""} onChange={(e) => patch({ site_location: e.target.value })} placeholder="Plant / city / address" data-testid="site-location" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold">Customer contact person</Label>
                  <Input className="rounded-2xl h-11 mt-1.5" value={job.customer_contact_person || ""} onChange={(e) => patch({ customer_contact_person: e.target.value })} placeholder="Name · phone · email" data-testid="customer-contact" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold">Scope of work</Label>
                  <Textarea rows={3} className="rounded-2xl mt-1.5" value={job.scope_of_work || ""} onChange={(e) => patch({ scope_of_work: e.target.value })} placeholder="What's being audited / inspected / serviced" data-testid="scope-of-work" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STAGE TABS — scheduling / execution / analysis / invoice each
            share the same "log + history" pattern with a different note kind. */}
        {["scheduling", "execution", "analysis", "invoice"].map((stageTab) => (
          <TabsContent key={stageTab} value={stageTab}>
            <Card className="rounded-3xl border-border/60">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-display text-lg font-extrabold">{STAGE_LABEL[stageTab]}</p>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{(stageTab === job.stage) ? "Current" : (STAGE_FLOW.indexOf(stageTab) < STAGE_FLOW.indexOf(job.stage)) ? "Completed" : "Upcoming"}</Badge>
                </div>

                {stageTab === "analysis" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Report status</Label>
                      <Select value={job.report_status || "not_started"} onValueChange={(v) => patch({ report_status: v })}>
                        <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="report-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="not_started">Not started</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="awaiting_review">Awaiting review</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <FileUploader jobId={jobId} kind="report" label="Upload final report" onUploaded={load} accept=".pdf,.doc,.docx" />
                    </div>
                  </div>
                )}

                {stageTab === "invoice" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Invoice status</Label>
                      <Select value={job.invoice_status || "not_started"} onValueChange={(v) => patch({ invoice_status: v })}>
                        <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="not_started">Pending</SelectItem>
                          <SelectItem value="drafted">Drafted</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Invoice date</Label>
                      <Input type="date" className="rounded-2xl h-11 mt-1.5" value={(job.invoice_date || "").slice(0, 10)} onChange={(e) => patch({ invoice_date: e.target.value || null })} data-testid="invoice-date" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Amount (₹)</Label>
                      <Input type="number" className="rounded-2xl h-11 mt-1.5" value={job.invoice_amount || ""} onChange={(e) => patch({ invoice_amount: Number(e.target.value) || null })} data-testid="invoice-amount" />
                    </div>
                    <div className="sm:col-span-3">
                      <FileUploader jobId={jobId} kind="invoice" label="Upload invoice" onUploaded={load} accept=".pdf,.png,.jpg,.jpeg" />
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold mb-2 uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Activity log</p>
                  <NoteList items={(stageTab === "scheduling" ? job.scheduling_notes : stageTab === "execution" ? job.execution_logs : stageTab === "analysis" ? job.analysis_notes : job.payment_followup_notes) || []} />
                  <AddNote kind={NOTE_KIND_BY_STAGE[stageTab]} jobId={jobId} onAdded={load} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* PHOTOS & DOCS */}
        <TabsContent value="files">
          <Card className="rounded-3xl border-border/60">
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <FileUploader jobId={jobId} kind="photo" label="Add site photo" onUploaded={load} accept="image/*" />
                <FileUploader jobId={jobId} kind="document" label="Add document" onUploaded={load} accept=".pdf,.doc,.docx,.xls,.xlsx" />
              </div>
              <div>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wider text-muted-foreground">Files ({(job.documents?.length || 0) + (job.photos?.length || 0)})</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[...(job.photos || []).map((p) => ({ ...p, kind: "photo" })), ...(job.documents || [])].map((f, i) => (
                    <li key={f.file_id || i} className="rounded-2xl border border-border/60 p-3 flex items-center justify-between gap-2" data-testid={`file-row-${f.file_id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{f.filename}</p>
                        <p className="text-[10px] text-muted-foreground">{f.kind} · {(f.size / 1024).toFixed(0)} KB · {fmtDate(f.uploaded_at)}</p>
                      </div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service-jobs/files/${f.file_id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                        <Download className="h-3.5 w-3.5" /> Open
                      </a>
                    </li>
                  ))}
                </ul>
                {(job.documents?.length || 0) + (job.photos?.length || 0) === 0 && (
                  <p className="text-xs text-muted-foreground italic">No files yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
